/**
 * The `Finance Buddy Data` spreadsheet: schema, creation and table I/O.
 *
 * Contract (same as garage-buddy): rows are keyed by id; columns are matched
 * by header name, never by index, so a user who reorders or adds columns
 * loses nothing. Unknown columns are carried through writes. The one use of
 * row position is replaceWhere, which deletes the rows it has just found;
 * like every read-modify-write here it holds a lock shared by every tab of
 * this browser (see exclusive), so no other tab moves rows in between. Two
 * devices writing at the same moment are not covered.
 *
 * Dates are written as YYYY-MM-DD with USER_ENTERED, so Sheets stores real
 * dates, and every date column gets the `yyyy-mm-dd` number format so Sheets
 * never re-renders them in the locale's form. Text is written with a leading
 * apostrophe so `0123`, `=SUM` or `+1 800` stay literal text.
 */
import { api } from './api'
import { create, findChild, SPREADSHEET } from './drive'
import { isIsoDate, parseSheetDate } from '../dates'
import { seedRows } from '../categories'

export const SPREADSHEET_NAME = 'Finance Buddy Data'
const SHEETS = 'https://sheets.googleapis.com/v4/spreadsheets'

/** 'raw' is a column the person added: whatever it holds is kept as it is. */
type ColumnType = 'text' | 'date' | 'timestamp' | 'number' | 'raw'

export const SCHEMA = {
  Documents: {
    file_id: 'text',
    name: 'text',
    original_name: 'text',
    doc_type: 'text',
    institution: 'text',
    account_id: 'text',
    period_start: 'date',
    period_end: 'date',
    status: 'text',
    confidence: 'number',
    review_reason: 'text',
    summary: 'text',
    error: 'text',
    model: 'text',
    processed_at: 'timestamp',
    /** When a run started reading the file; a fresh one keeps other tabs off it. */
    claimed_at: 'timestamp',
    link: 'text',
    md5: 'text',
    duplicate_of: 'text',
  },
  Accounts: {
    account_id: 'text',
    institution: 'text',
    kind: 'text',
    name: 'text',
    last4: 'text',
    opened: 'date',
    closed: 'date',
    notes: 'text',
  },
  Balances: {
    account_id: 'text',
    as_of: 'date',
    balance: 'number',
    currency: 'text',
    credit_limit: 'number',
    apr: 'number',
    source_file_id: 'text',
    payment_due_date: 'date',
    minimum_payment: 'number',
    /** 'on', 'off', or empty when the statement doesn't say. */
    autopay: 'text',
  },
  Transactions: {
    txn_id: 'text',
    account_id: 'text',
    date: 'date',
    description: 'text',
    merchant: 'text',
    amount: 'number',
    category: 'text',
    source_file_id: 'text',
  },
  Holdings: {
    account_id: 'text',
    as_of: 'date',
    symbol: 'text',
    name: 'text',
    quantity: 'number',
    price: 'number',
    value: 'number',
    cost_basis: 'number',
    asset_class: 'text',
    expense_ratio: 'number',
    source_file_id: 'text',
  },
  Receipts: {
    file_id: 'text',
    date: 'date',
    merchant: 'text',
    total: 'number',
    tax: 'number',
    items_json: 'text',
    matched_txn_id: 'text',
  },
  Profile: {
    key: 'text',
    value: 'text',
    source: 'text',
    basis: 'text',
    updated_at: 'timestamp',
  },
  Recommendations: {
    rec_id: 'text',
    created_at: 'timestamp',
    area: 'text',
    title: 'text',
    detail: 'text',
    est_annual_value: 'number',
    status: 'text',
    priority: 'text',
    next_step: 'text',
  },
  Analyses: {
    run_at: 'timestamp',
    model: 'text',
    overview: 'text',
    findings_json: 'text',
    recs_json: 'text',
  },
  Manual: {
    name: 'text',
    type: 'text',
    category: 'text',
    value: 'number',
    as_of: 'date',
    rate: 'number',
    notes: 'text',
  },
  Corrections: {
    txn_key: 'text',
    txn_id: 'text',
    category: 'text',
    one_off: 'text',
    updated_at: 'timestamp',
  },
  /** See categories.ts. Seeded with the built-ins when the tab is created. */
  Categories: {
    category_id: 'text',
    category: 'text',
    subcategory: 'text',
    type: 'text',
  },
  Rules: {
    rule_id: 'text',
    match: 'text',
    account_id: 'text',
    category: 'text',
    one_off: 'text',
    created_at: 'timestamp',
  },
  Usage: {
    at: 'timestamp',
    kind: 'text',
    ref: 'text',
    model: 'text',
    input_tokens: 'number',
    cache_write_tokens: 'number',
    cache_read_tokens: 'number',
    output_tokens: 'number',
    web_searches: 'number',
    cost_usd: 'number',
    seconds: 'number',
  },
  Questions: {
    asked_at: 'timestamp',
    question: 'text',
    answer: 'text',
    model: 'text',
  },
} as const satisfies Record<string, Record<string, ColumnType>>

export type TabName = keyof typeof SCHEMA
export type Cell = string | number | null | undefined
export type Row = Record<string, Cell>

export interface Table {
  tab: TabName
  headers: string[]
  rows: Row[]
}


const range = (tab: string, a1: string) => encodeURIComponent(`'${tab}'!${a1}`)

/** A1 column letters for a 0-based index: 0 → A, 25 → Z, 26 → AA. */
export function columnLetter(index: number): string {
  let s = ''
  for (let n = index + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s
  return s
}

/**
 * The rows to delete, as 0-based [start, end) sheet row indices, last first
 * so each deletion leaves the earlier ones where they were. `cells` is one
 * column read from row 2 down, so cells[i] is sheet index i + 1.
 */
export function matchingSpans(cells: unknown[][], value: string): [number, number][] {
  const spans: [number, number][] = []
  cells.forEach((cell, i) => {
    if (String(cell[0] ?? '') !== value) return
    const last = spans.at(-1)
    if (last && last[1] === i + 1) last[1] = i + 2
    else spans.push([i + 1, i + 2])
  })
  return spans.reverse()
}

function columnType(tab: TabName, header: string): ColumnType {
  return (SCHEMA[tab] as Record<string, ColumnType>)[header] ?? 'raw'
}

/**
 * One cell value as it should be sent with USER_ENTERED. Anything the app
 * can't read as its column's type goes back as literal text, exactly as it
 * was: Sheets never gets to re-guess "03/10/2026" as March or October.
 */
export function toCell(type: ColumnType, value: Cell): string | number | boolean {
  if (value === null || value === undefined || value === '') return ''
  // A checkbox or number in a column the person added stays a checkbox or number.
  if (type === 'raw' && (typeof value === 'number' || typeof value === 'boolean')) return value
  if (type === 'number') {
    const n = typeof value === 'number' ? value : Number(value)
    return Number.isFinite(n) ? n : `'${String(value)}`
  }
  if (type === 'date') return isIsoDate(String(value)) ? String(value) : `'${String(value)}`
  return `'${String(value)}`
}

/** One cell value as read back (UNFORMATTED_VALUE + FORMATTED_STRING dates). */
export function fromCell(type: ColumnType, value: unknown): Cell {
  if (value === '' || value === null || value === undefined) return null
  if (type === 'date') return parseSheetDate(value) ?? String(value)
  if (type === 'number') {
    if (typeof value === 'number') return value
    // Text the person typed ("350k") stays text rather than becoming NaN.
    const n = Number(value)
    return Number.isFinite(n) ? n : String(value)
  }
  if (type === 'raw' && (typeof value === 'number' || typeof value === 'boolean')) return value as Cell
  return String(value)
}

/** Formulas a row had when read for a rewrite, with the values they showed, by header. */
const FORMULAS = Symbol('formulas')
type Formulas = Record<string, { formula: string; shown: Cell }>
type ReadRow = Row & { [FORMULAS]?: Formulas }

/** Rows from a values grid: header row first, blank rows dropped, formulas kept beside the values when given. */
export function rowsFrom(tab: TabName, values: unknown[][], formulas?: unknown[][]): { headers: string[]; rows: Row[] } {
  const [headerRow = [], ...body] = values
  const headers = headerRow.map(String)
  const rows: Row[] = []
  body.forEach((r, i) => {
    if (!r.some((v) => v !== '' && v !== null)) return
    const row: ReadRow = Object.fromEntries(headers.map((h, j) => [h, fromCell(columnType(tab, h), r[j])]))
    const f = formulas?.[i + 1]
    if (f) {
      const kept: Formulas = {}
      headers.forEach((h, j) => {
        const cell = f[j]
        if (typeof cell === 'string' && cell.startsWith('=')) kept[h] = { formula: cell, shown: row[h] }
      })
      if (Object.keys(kept).length) row[FORMULAS] = kept
    }
    rows.push(row)
  })
  return { headers, rows }
}

/**
 * The values grid that rewrites a tab. A cell that held a formula gets the
 * formula back, unless the app changed what it showed: a rewrite never turns
 * a person's formula into a frozen number.
 */
export function cellsFor(tab: TabName, headers: string[], rows: Row[]): (string | number | boolean)[][] {
  return [
    headers,
    ...rows.map((r) =>
      headers.map((h) => {
        const kept = (r as ReadRow)[FORMULAS]?.[h]
        if (kept && kept.shown === r[h]) return kept.formula
        return toCell(columnType(tab, h), r[h])
      }),
    ),
  ]
}

/** One name for every tab of this browser: read-modify-writes take turns. */
const SHEET_LOCK = 'finance-buddy:sheet'

/**
 * Runs `task` while no other tab of this browser is changing the Sheet. Web
 * Locks are queued fairly and released if a tab closes; where they are not
 * available (tests) the task just runs.
 */
export function exclusive<T>(name: string, task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks
  return locks ? (locks.request(name, task) as Promise<T>) : task()
}

export class Database {
  constructor(readonly spreadsheetId: string, readonly url: string) {}

  async read(tab: TabName, withFormulas = false): Promise<Table> {
    const get = (render: string) =>
      api.get<{ values?: unknown[][] }>(`${SHEETS}/${this.spreadsheetId}/values/${range(tab, 'A1:ZZ')}?valueRenderOption=${render}&dateTimeRenderOption=FORMATTED_STRING`)
    const [res, formulas] = await Promise.all([get('UNFORMATTED_VALUE'), withFormulas ? get('FORMULA') : undefined])
    return { tab, ...rowsFrom(tab, res.values ?? [], formulas?.values) }
  }

  /**
   * Several tabs in one request. Sheets allows 60 read requests a minute per
   * person; a recount reads a dozen tabs, so one request each ran out after a
   * few corrections in a row.
   */
  async readMany<T extends TabName>(tabs: readonly T[]): Promise<Record<T, Table>> {
    const ranges = tabs.map((t) => `ranges=${range(t, 'A1:ZZ')}`).join('&')
    const res = await api.get<{ valueRanges: { values?: unknown[][] }[] }>(
      `${SHEETS}/${this.spreadsheetId}/values:batchGet?${ranges}&valueRenderOption=UNFORMATTED_VALUE&dateTimeRenderOption=FORMATTED_STRING`,
    )
    return Object.fromEntries(tabs.map((tab, i) => [tab, { tab, ...rowsFrom(tab, res.valueRanges[i]?.values ?? []) }])) as unknown as Record<T, Table>
  }

  /** Rewrites the whole tab: header row as-is, then `rows`, then clears leftovers. */
  async write(table: Table): Promise<void> {
    return exclusive(SHEET_LOCK, () => this.put(table))
  }

  /**
   * Reads a tab, lets `change` edit it, and writes it back, with no other
   * tab's write in between. For anything that rewrites a tab from what it read.
   */
  async modify(tab: TabName, change: (table: Table) => void): Promise<Table> {
    return exclusive(SHEET_LOCK, async () => {
      // With formulas, so the rewrite can put back any the person added.
      const table = await this.read(tab, true)
      change(table)
      await this.put(table)
      return table
    })
  }

  private async put(table: Table): Promise<void> {
    const { tab, headers, rows } = table
    const values = cellsFor(tab, headers, rows)
    await api.put(`${SHEETS}/${this.spreadsheetId}/values/${range(tab, 'A1')}?valueInputOption=USER_ENTERED`, {
      values,
    })
    await api.post(`${SHEETS}/${this.spreadsheetId}/values/${range(tab, `A${values.length + 1}:ZZ`)}:clear`, {})
  }

  /**
   * Adds rows at the end without rewriting the tab, so two writers at once
   * (a document being read while a question is answered) can't lose a row.
   */
  async append(tab: TabName, rows: Row[]): Promise<void> {
    const head = await api.get<{ values?: unknown[][] }>(`${SHEETS}/${this.spreadsheetId}/values/${range(tab, '1:1')}`)
    const headers = (head.values?.[0] ?? []).map(String)
    const values = rows.map((r) => headers.map((h) => toCell(columnType(tab, h), r[h])))
    await api.post(
      `${SHEETS}/${this.spreadsheetId}/values/${range(tab, 'A1')}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`,
      { values },
    )
  }

  /**
   * Replaces every row whose `column` equals `value` with `replacement`. Reads
   * only that column, deletes the matching rows (none, for a document read the
   * first time) and appends the replacement, so the cost follows the size of
   * the replacement, not of the tab, and the other rows are never rewritten.
   */
  async replaceWhere(tab: TabName, column: string, value: string, replacement: Row[]): Promise<void> {
    return exclusive(SHEET_LOCK, () => this.replace(tab, column, value, replacement))
  }

  private async replace(tab: TabName, column: string, value: string, replacement: Row[]): Promise<void> {
    const head = await api.get<{ values?: unknown[][] }>(`${SHEETS}/${this.spreadsheetId}/values/${range(tab, '1:1')}`)
    const col = (head.values?.[0] ?? []).map(String).indexOf(column)
    if (col < 0) throw new Error(`${tab} has no ${column} column`)
    const letter = columnLetter(col)
    const res = await api.get<{ values?: unknown[][] }>(
      `${SHEETS}/${this.spreadsheetId}/values/${range(tab, `${letter}2:${letter}`)}?valueRenderOption=UNFORMATTED_VALUE`,
    )
    const spans = matchingSpans(res.values ?? [], value)
    if (spans.length) {
      const meta = await api.get<{ sheets: { properties: SheetProps & { gridProperties: { rowCount: number } } }[] }>(
        `${SHEETS}/${this.spreadsheetId}?fields=sheets.properties(sheetId,title,gridProperties.rowCount)`,
      )
      const sheet = meta.sheets.map((s) => s.properties).find((p) => p.title === tab)
      if (!sheet) throw new Error(`No ${tab} tab`)
      const deleting = spans.reduce((n, [start, end]) => n + end - start, 0)
      if (deleting >= sheet.gridProperties.rowCount - 1) {
        // Sheets refuses to delete every row below a frozen header; empty them instead.
        await api.post(`${SHEETS}/${this.spreadsheetId}/values/${range(tab, 'A2:ZZ')}:clear`, {})
      } else {
        await api.post(`${SHEETS}/${this.spreadsheetId}:batchUpdate`, {
          requests: spans.map(([startIndex, endIndex]) => ({
            deleteDimension: { range: { sheetId: sheet.sheetId, dimension: 'ROWS', startIndex, endIndex } },
          })),
        })
      }
    }
    if (replacement.length) await this.append(tab, replacement)
  }

  /**
   * Inserts or updates rows keyed by `key`. With `fillOnly`, an existing row
   * only gains values in cells that are empty — so a hand edit survives.
   */
  async upsert(tab: TabName, key: string, incoming: Row[], fillOnly = false): Promise<void> {
    await this.modify(tab, (table) => {
      for (const row of incoming) {
        const existing = table.rows.find((r) => r[key] === row[key])
        if (!existing) {
          table.rows.push(row)
          continue
        }
        for (const [k, v] of Object.entries(row)) {
          if (v === null || v === undefined) continue
          if (fillOnly && existing[k] !== null && existing[k] !== undefined && existing[k] !== '') continue
          existing[k] = v
        }
      }
    })
  }
}

interface SheetProps {
  sheetId: number
  title: string
}

/**
 * An empty Categories tab gets the built-ins. Checked on every open, not only
 * when the tab is created: a first open that failed after creating the tab
 * left it with headers only. Under the lock, so two loads don't both write.
 * An empty tab already reads as the built-ins, so this changes no figure.
 */
export async function seedCategories(db: Database): Promise<void> {
  await exclusive(SHEET_LOCK, async () => {
    if ((await db.read('Categories')).rows.length === 0) await db.append('Categories', seedRows())
  })
}

/** Finds or creates the spreadsheet in the root folder, then repairs its tabs. */
export async function openDatabase(rootFolderId: string): Promise<Database> {
  const file =
    (await findChild(rootFolderId, SPREADSHEET_NAME, SPREADSHEET)) ??
    (await create(rootFolderId, SPREADSHEET_NAME, SPREADSHEET))
  await ensureTabs(file.id)
  const db = new Database(file.id, file.webViewLink ?? `https://docs.google.com/spreadsheets/d/${file.id}`)
  await seedCategories(db)
  return db
}

async function sheetList(spreadsheetId: string): Promise<SheetProps[]> {
  const meta = await api.get<{ sheets: { properties: SheetProps }[] }>(
    `${SHEETS}/${spreadsheetId}?fields=sheets.properties(sheetId,title)`,
  )
  return meta.sheets.map((s) => s.properties)
}

async function ensureTabs(spreadsheetId: string): Promise<void> {
  const tabs = Object.keys(SCHEMA) as TabName[]
  let sheets = await sheetList(spreadsheetId)
  const missing = tabs.filter((t) => !sheets.some((s) => s.title === t))
  if (missing.length) {
    await api.post(`${SHEETS}/${spreadsheetId}:batchUpdate`, {
      requests: missing.map((title) => ({ addSheet: { properties: { title, gridProperties: { frozenRowCount: 1 } } } })),
    })
    // A new spreadsheet starts with an empty "Sheet1"; drop it once ours exist.
    const stray = sheets.find((s) => s.title === 'Sheet1')
    if (stray) {
      await api.post(`${SHEETS}/${spreadsheetId}:batchUpdate`, { requests: [{ deleteSheet: { sheetId: stray.sheetId } }] })
    }
    sheets = await sheetList(spreadsheetId)
  }

  const ranges = tabs.map((t) => `ranges=${range(t, '1:1')}`).join('&')
  const res = await api.get<{ valueRanges: { values?: string[][] }[] }>(
    `${SHEETS}/${spreadsheetId}/values:batchGet?${ranges}`,
  )

  const valueUpdates: { range: string; values: string[][] }[] = []
  const formatRequests: unknown[] = []
  tabs.forEach((tab, i) => {
    const current = res.valueRanges[i]?.values?.[0] ?? []
    const wanted = Object.keys(SCHEMA[tab])
    const headers = [...current, ...wanted.filter((h) => !current.includes(h))]
    if (headers.length === current.length) return
    valueUpdates.push({ range: `'${tab}'!A1`, values: [headers] })

    const sheetId = sheets.find((s) => s.title === tab)!.sheetId
    formatRequests.push({
      repeatCell: {
        range: { sheetId, startRowIndex: 0, endRowIndex: 1 },
        cell: { userEnteredFormat: { textFormat: { bold: true } } },
        fields: 'userEnteredFormat.textFormat.bold',
      },
    })
    headers.forEach((h, col) => {
      if (columnType(tab, h) !== 'date') return
      formatRequests.push({
        repeatCell: {
          range: { sheetId, startRowIndex: 1, startColumnIndex: col, endColumnIndex: col + 1 },
          cell: { userEnteredFormat: { numberFormat: { type: 'DATE', pattern: 'yyyy-mm-dd' } } },
          fields: 'userEnteredFormat.numberFormat',
        },
      })
    })
  })

  if (valueUpdates.length) {
    await api.post(`${SHEETS}/${spreadsheetId}/values:batchUpdate`, { valueInputOption: 'RAW', data: valueUpdates })
  }
  if (formatRequests.length) {
    await api.post(`${SHEETS}/${spreadsheetId}:batchUpdate`, { requests: formatRequests })
  }
}

